import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock, Unlock, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { title: "", slug: "", team: "Growth", duration: "3 Months", location: "Remote", form_url: "", status: "open", order: 0 };

const Field = ({ label, id, ...props }) => (
  <label className="block">
    <span className="text-xs font-mono uppercase tracking-widest text-[var(--muted)]">{label}</span>
    <input id={id} data-testid={id} {...props} className="mt-1 w-full rounded-xl border border-black/10 bg-white/80 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--saffron)]" />
  </label>
);

const JobEditor = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, order: Number(f.order) || 0 };
      const { data } = f.id ? await api.put(`/admin/jobs/${f.id}`, payload) : await api.post("/admin/jobs", payload);
      toast.success(f.id ? "Opening updated" : "Opening added");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="glass rounded-3xl p-6 sm:p-8 space-y-4" data-testid="admin-job-editor">
      <h2 className="font-display font-bold text-2xl text-[var(--ink)]">{f.id ? "Edit opening" : "New opening"}</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Role title" id="job-title" value={f.title} onChange={set("title")} required placeholder="Growth Marketing Intern" />
        <Field label="Slug (auto from title if blank)" id="job-slug" value={f.slug} onChange={set("slug")} />
        <Field label="Team" id="job-team" value={f.team} onChange={set("team")} />
        <Field label="Duration" id="job-duration" value={f.duration} onChange={set("duration")} />
        <Field label="Location" id="job-location" value={f.location} onChange={set("location")} />
        <Field label="Display order" id="job-order" type="number" value={f.order} onChange={set("order")} />
      </div>
      <Field label="Google Form URL (opens in new tab on Apply)" id="job-form-url" type="url" value={f.form_url} onChange={set("form_url")} required placeholder="https://forms.gle/..." />
      <label className="block">
        <span className="text-xs font-mono uppercase tracking-widest text-[var(--muted)]">Status</span>
        <select value={f.status} onChange={set("status")} data-testid="job-status" className="mt-1 w-full rounded-xl border border-black/10 bg-white/80 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--saffron)]">
          <option value="open">Open — shown on /careers</option>
          <option value="closed">Closed — hidden from /careers</option>
        </select>
      </label>
      <div className="flex gap-3">
        <button className="btn-saffron !py-2.5 !px-6 text-sm" disabled={busy} data-testid="job-save">{busy ? "Saving…" : "Save"}</button>
        <button type="button" onClick={onCancel} className="btn-ghost !py-2.5 !px-6 text-sm" data-testid="job-cancel">Cancel</button>
      </div>
    </form>
  );
};

export const JobsSettings = () => {
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/jobs").then((r) => setJobs(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const toggle = async (j) => {
    await api.put(`/admin/jobs/${j.id}`, { status: j.status === "open" ? "closed" : "open" });
    toast.success(j.status === "open" ? "Opening closed" : "Opening reopened");
    load();
  };
  const remove = async (j) => {
    if (!window.confirm(`Delete "${j.title}"? Closing it instead keeps the record.`)) return;
    await api.delete(`/admin/jobs/${j.id}`);
    toast.success("Deleted");
    load();
  };

  if (editing) return <JobEditor initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  return (
    <div className="space-y-6" data-testid="jobs-settings">
      <div className="flex justify-end">
        <button onClick={() => setEditing(EMPTY)} className="btn-saffron !py-2.5 !px-5 text-sm" data-testid="admin-new-job"><Plus size={16} /> New opening</button>
      </div>
      <ul className="space-y-3" data-testid="admin-job-list">
        {jobs.length === 0 && <li className="glass rounded-2xl p-5 text-sm text-[var(--muted)]" data-testid="admin-jobs-empty">No openings yet.</li>}
        {jobs.map((j) => (
          <li key={j.id} className="glass rounded-2xl p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`admin-job-row-${j.slug}`}>
            <div className="flex-1 min-w-[220px]">
              <p className="font-bold text-[var(--ink)]">{j.title}</p>
              <p className="text-xs font-mono text-[var(--muted)]">{j.team} · {j.duration} · {j.location} · <span className={j.status === "open" ? "text-[var(--green-deep)]" : "text-red-600"} data-testid={`admin-job-status-${j.slug}`}>{j.status === "open" ? "Open" : "Closed"}</span></p>
              <a href={j.form_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--saffron-deep)] underline underline-offset-2 mt-1 break-all">{j.form_url} <ExternalLink size={11} /></a>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggle(j)} className="p-2 rounded-full border border-black/10 hover:bg-black/5" title={j.status === "open" ? "Close opening" : "Reopen"} data-testid={`admin-job-toggle-${j.slug}`}>{j.status === "open" ? <Lock size={16} /> : <Unlock size={16} />}</button>
              <button onClick={() => setEditing(j)} className="p-2 rounded-full border border-black/10 hover:bg-black/5" data-testid={`admin-job-edit-${j.slug}`}><Pencil size={16} /></button>
              <button onClick={() => remove(j)} className="p-2 rounded-full border border-black/10 hover:bg-red-50 text-red-600" data-testid={`admin-job-delete-${j.slug}`}><Trash2 size={16} /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
